import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Switch,
  Typography,
  Upload,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import requets from "../../api";
import { uploadEntityImage } from "../../api/service/cdn/cdn";
import { useTranslation } from "react-i18next";
import { useAppAuth } from "../../context/use-app-auth";
import LocationPicker from "./components/LocationPicker";

const { Title } = Typography;

const normalizeUploadFile = (e) => {
  if (Array.isArray(e)) return e;
  return e?.fileList;
};

export default function DeviceNew() {
  const auth = useAppAuth();
  const req = useMemo(
    () => requets(auth.user?.access_token),
    [auth.user?.access_token],
  );
  const { t } = useTranslation("global");
  const [messageSend, contextHolder] = message.useMessage();
  const [products, setProducts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [mapPosition, setMapPosition] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    req
      .get("/product")
      .then((response) => {
        const raw = response.data;
        const dataArray = Array.isArray(raw) ? raw : raw?.data || [];
        setProducts(
          dataArray.map((item) => ({ value: item.uuid, label: item.name })),
        );
      })
      .catch(() => {});
  }, [req]);

  const onFinish = async (value) => {
    setUploading(true);
    try {
      const iconFile = value.icon?.[0];

      // Create device first to establish the record, then upload image
      const response = await req.post("/device/", {
        name: value.name,
        devEui: value.devEui,
        application: value.application,
        bucket: value.bucket,
        productUuid: value.productUuid,
        isActive: value.isActive ?? true,
        coordinates: (mapPosition ?? []).map(String),
      });

      if (iconFile?.originFileObj && value.devEui) {
        await uploadEntityImage(req, `/device/${value.devEui}/image`, iconFile.originFileObj);
      }

      if (response.status === 201) {
        messageSend.success(t("device.sucessCreated"));
        form.resetFields();
        setMapPosition(null);
      }
    } catch {
      messageSend.error(t("deviceEdit.error"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="layout-content">
      {contextHolder}
      <Row className="rowgap-vbox" gutter={[24, 0]}>
        <Col xs={24} className="mb-24">
          <Card
            bordered={false}
            title={
              <Title level={4} style={{ margin: 0 }}>
                {t("deviceNew.deviceTabs")}
              </Title>
            }
          >
            <Form layout="vertical" form={form} onFinish={onFinish}>
              <Row wrap gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                <Col xs={24} sm={12} md={8} lg={7}>
                  <Form.Item
                    label={t("deviceNew.deviceName")}
                    name="name"
                    rules={[
                      { required: true, message: t("formValidator.mandatory") },
                    ]}
                  >
                    <Input placeholder={t("deviceNew.placeHolderDeviceName")} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={7}>
                  <Form.Item
                    label={t("deviceNew.deviceEui")}
                    name="devEui"
                    rules={[
                      { required: true, message: t("formValidator.mandatory") },
                    ]}
                  >
                    <Input placeholder={t("deviceNew.placeHolderDeviceEui")} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={7}>
                  <Form.Item
                    label={t("deviceNew.application")}
                    name="application"
                    rules={[
                      { required: true, message: t("formValidator.mandatory") },
                    ]}
                  >
                    <Input
                      placeholder={t("deviceNew.placeHolderDeviceApplication")}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row wrap gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                <Col xs={24} sm={12} md={8} lg={7}>
                  <Form.Item
                    label={t("deviceNew.bucket")}
                    name="bucket"
                    rules={[
                      { required: true, message: t("formValidator.mandatory") },
                    ]}
                  >
                    <Input
                      placeholder={t("deviceNew.placeHolderDeviceBucket")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={7}>
                  <Form.Item
                    label={t("deviceNew.productUuid")}
                    name="productUuid"
                    rules={[
                      { required: true, message: t("formValidator.mandatory") },
                    ]}
                  >
                    <Select
                      options={products}
                      size="large"
                      allowClear
                      placeholder={t("deviceNew.productUuid")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={4} lg={3}>
                  <Form.Item
                    label={t("zones.state")}
                    name="isActive"
                    valuePropName="checked"
                    initialValue={true}
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
              <Row wrap gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                <Col xs={24}>
                  <Form.Item label={t("deviceNew.coordinates") || "Ubicación en el mapa"}>
                    <LocationPicker
                      position={mapPosition}
                      onSelect={(coords) => {
                        setMapPosition(coords);
                        form.setFieldsValue({ latitud: coords[0], longitud: coords[1] });
                      }}
                      t={t}
                    />
                  </Form.Item>
                  <Form.Item name="latitud" hidden><Input /></Form.Item>
                  <Form.Item name="longitud" hidden><Input /></Form.Item>
                </Col>
              </Row>
              <Row wrap gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                <Col xs={24} sm={8} md={6} lg={4}>
                  <Form.Item
                    label={t("products.tableColumn.icon")}
                    name="icon"
                    valuePropName="fileList"
                    getValueFromEvent={normalizeUploadFile}
                  >
                    <Upload
                      listType="picture-card"
                      maxCount={1}
                      beforeUpload={() => false}
                      accept=".jpg,.jpeg,.png,.webp"
                    >
                      <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>
                          {t("products.uploadButton")}
                        </div>
                      </div>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item>
                <Button
                  shape="round"
                  htmlType="submit"
                  type="primary"
                  loading={uploading}
                >
                  {t("deviceEdit.saveChange")}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
